import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useInView } from "react-intersection-observer";
import { Box, IconButton, Typography, CircularProgress } from "@mui/material";

import { getEntityProfileRoute } from "../../../../routers/routeUtils";

import { useInfiniteAchievementPosts } from "../../../Entity/api/queries/useInfiniteAchievementPosts";
import AdvertisementCol from "../../../Ads/components/AdvertisementCol";
import { Post } from "../../../Feeds/types/post.type";
import PostCard from "../../../Feeds/components/Posts";

import { EntityBackToProfileIcon } from "../../assets/icons/entity-icons";
import EmptyAchievements from "../../assets/images/empty-entity-achievements.png";

import { useEntityDataFormatter } from "../../../Entity/hooks/useEntityDataFormatter";
import { useEntityDetails } from "../../../Entity/api/queries/useEntityDetails";

import AchievementsTab from "./AchievementsTab";

const POSTS_PER_PAGE = 2;

const EntityFullAchievements = () => {
  const { t } = useTranslation("profiles", {
    keyPrefix: "entityAchievements",
  });
  const navigate = useNavigate();
  const { entityId, achievementId } = useParams();

  const { data: entityData, isLoading: isEntityLoading, isError: isEntityError } = useEntityDetails(entityId!);
  const {
    fullName: entityFullName,
    firstTwoChr,
    countryName,
    role,
    cityName,
    media,
    isFollowing,
  } = useEntityDataFormatter(entityData);

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteAchievementPosts(
    entityId!,
    achievementId!,
    POSTS_PER_PAGE
  );

  const { ref, inView } = useInView();

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage, isFetchingNextPage]);

  const allPosts = data?.pages.flatMap((page) => page.data.items) ?? [];

  const [achievementName, setAchievementName] = useState("");

  const handleBack = () => {
    // navigate(getEntityProfileRoute(entityId!))
    navigate(-1);
  };

  return (
    <Box sx={styles.container}>
      <Box sx={styles.entityProfileContainer}>
        <Typography sx={styles.title}>
          <IconButton size="small" onClick={handleBack}>
            <EntityBackToProfileIcon />
          </IconButton>
          {t("achievements")}
        </Typography>
        <AchievementsTab isLoading={isLoading} setCurrentAchievementName={setAchievementName} />

        {allPosts.length === 0 && !isLoading && (
          <NoAchievements entityName={entityFullName} achievementName={achievementName} />
        )}
        {allPosts.length > 0 && <AchievementsList posts={allPosts} />}

        <Box ref={ref} sx={{ height: "fit-content", width: "100%" }}>
          {isFetchingNextPage && (
            <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
              <CircularProgress />
            </Box>
          )}
        </Box>
      </Box>
      <Box sx={styles.advertisementContainer}>
        <AdvertisementCol />
      </Box>
    </Box>
  );
};

export default EntityFullAchievements;

const NoAchievements = ({ entityName, achievementName }: { entityName: string; achievementName: string }) => {
  const { t } = useTranslation("profiles", {
    keyPrefix: "entityAchievements",
  });

  return (
    <Box sx={styles.noAchievementsContainer}>
      <Box component="img" sx={styles.noAchievementsImage} src={EmptyAchievements} alt="empty-achievements" />
      <Typography sx={styles.noAchievementsText}>{t("noAchievements", { entityName, achievementName })}</Typography>
    </Box>
  );
};

const AchievementsList = ({ posts }: { posts: Post[] }) => {
  return (
    <Box sx={styles.achievementsListContainer}>
      {posts.map((post) => (
        <PostCard key={post.id} post={post} onClickOnMedia={() => {}} />
      ))}
    </Box>
  );
};

const styles = {
  container: { display: "flex", gap: "16px" },
  entityProfileContainer: { width: "100%", display: "flex", flexDirection: "column", gap: "16px" },
  advertisementContainer: { display: { xs: "none", lg: "block" } },
  title: {
    color: "var(--black-100, #1C1C1C)",
    fontFamily: "Montserrat",
    fontSize: "20px",
    fontStyle: "normal",
    fontWeight: 800,
    lineHeight: "normal",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    mb: "24px",
  },
  achievementsListContainer: { display: "flex", flexDirection: "column", gap: "16px" },
  noAchievementsContainer: {
    display: "flex",
    justifyContent: "center",
    flexDirection: "column",
    alignItems: "center",
    gap: "12px",
    py: 4,
  },
  noAchievementsImage: {
    width: "73.51px",
    height: "73.485px",
  },
  noAchievementsText: {
    color: "var(--Dark---600, #707070)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "24px",
    textAlign: "center",
  },
};
